import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import { LockOutlined, EmailOutlined } from '@mui/icons-material';

const API_URL = '/api/auth';

const EnhancedLogin = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Default to true
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isAuth = localStorage.getItem('isAuthenticated');
    if (token && isAuth === 'true') {
      navigate('/dashboard');
    }
  }, [navigate]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/login`, {
        email,
        password
      });
      
      const { access_token, refresh_token, user } = response.data;
      
      // ALWAYS store in localStorage for consistency with ProtectedRoute
      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', 'true');
      
      console.log('Login successful, user data:', user);
      console.log('Token stored:', access_token ? 'yes' : 'no');
      console.log('isAuthenticated:', localStorage.getItem('isAuthenticated'));
      
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Login error:', err);
      
      let errorMessage = 'Login failed. Please try again.';
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage = err.response.data.error;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4} alignItems="center" justifyContent="center">
        <Grid item xs={12} md={5}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                Welcome Back
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Log in to your Salon Logic account
              </Typography>
            </Box>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: <EmailOutlined sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: <LockOutlined sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      value="remember" 
                      color="primary" 
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                  }
                  label="Remember me"
                />
                
                <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    Forgot password?
                  </Typography>
                </Link>
              </Box>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                New to SalonLogic Pro?
              </Typography>
              <Button
                component={Link}
                to="/register"
                variant="outlined"
                color="primary"
                fullWidth
                size="large"
                sx={{ py: 1.5 }}
            >
                Create Account
            </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ pl: { md: 4 } }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
              Salon Logic Pricing Calculator
            </Typography>
            
            <Typography variant="body1" paragraph>
              Professional pricing tools for salon owners. Part of the SalonLogic Pro business suite.
            </Typography>
            
            <Box sx={{ my: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                How It Works:
              </Typography>
              
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2, height: '100%', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      margin: '0 auto 16px auto',
                      fontWeight: 'bold',
                      fontSize: 20
                    }}>
                      1
                    </Box>
                    <Typography variant="h6" gutterBottom>Input Your Costs</Typography>
                    <Typography variant="body2">Easily enter your business expenses, staff costs, and product details.</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2, height: '100%', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      margin: '0 auto 16px auto',
                      fontWeight: 'bold',
                      fontSize: 20
                    }}>
                      2
                    </Box>
                    <Typography variant="h6" gutterBottom>Set Up Services</Typography>
                    <Typography variant="body2">Define your services with time requirements and product usage.</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center', p: 2, height: '100%', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      margin: '0 auto 16px auto',
                      fontWeight: 'bold',
                      fontSize: 20
                    }}>
                      3
                    </Box>
                    <Typography variant="h6" gutterBottom>Get Optimal Pricing</Typography>
                    <Typography variant="body2">Receive calculated prices that ensure profitability and competitiveness.</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
            <Box sx={{ mt: 5, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                "This calculator transformed my salon business. Finally, I can set prices that actually make me money!"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                — Sarah T., Salon Owner
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 8, mb: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          &copy; {new Date().getFullYear()} SalonLogic Pro Pricing Calculator. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
};

export default EnhancedLogin;
